<script setup lang="ts">
import {computed} from 'vue'
import type {DashboardReviewBucket} from '@shared/types'
import breakImage from '@/assets/dashboard-break.svg?url'

const props = defineProps<{reviews: DashboardReviewBucket[]; total: number; completed: number}>()

const groups = computed(() => {
  const count = (keys: string[]) =>
    props.reviews.filter((item) => keys.includes(item.key)).reduce((sum, item) => sum + item.count, 0)
  return [
    {key: 'overdue', count: count(['overdue']), class: 'bg-amber-400'},
    {key: 'today', count: count(['today']), class: 'bg-violet-500'},
    {key: 'week', count: count(['tomorrow', 'threeDays', 'week']), class: 'bg-blue-500'},
    {key: 'month', count: count(['twoWeeks', 'month', 'later']), class: 'bg-emerald-500'},
  ]
})

const completed = computed(() => Math.max(0, Math.min(props.completed, props.total)))
const progressShare = computed(() => {
  if (!props.total || completed.value >= props.total * 0.35) return completed.value ? 1 : 0
  return Math.max(0.5, Math.min(0.6, Math.sqrt(completed.value / props.total) * 1.7))
})
</script>

<template>
  <div>
    <div class="relative h-2.5 w-full">
      <div class="stack-track flex h-full w-full overflow-hidden rounded-full">
        <span
          v-for="group in groups"
          :key="group.key"
          :class="group.class"
          :style="{width: completed ? `${(group.count / Math.max(completed, 1)) * progressShare * 100}%` : '0%'}" />
        <span
          v-if="progressShare < 1"
          class="stack-gray flex h-full shrink-0"
          :style="{width: `${(1 - progressShare) * 100}%`}">
          <span class="stack-gray-segment" />
          <span class="stack-gray-gap" />
          <span class="stack-gray-segment" />
        </span>
      </div>
      <span
        v-if="progressShare < 1"
        class="stack-break"
        :style="{left: `${(progressShare + (1 - progressShare) / 2) * 100}%`}">
        <img :src="breakImage" alt="" />
      </span>
    </div>
    <div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      <span v-for="group in groups" :key="`legend-${group.key}`" class="flex items-center gap-1">
        <span class="size-2 rounded-full" :class="group.class" />
        {{ $t(`home.dashboard.review.${group.key}`) }} · {{ group.count }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.stack-break {
  position: absolute;
  top: 50%;
  display: flex;
  width: 18px;
  height: 36px;
  z-index: 1;
  align-items: center;
  justify-content: space-between;
  transform: translate(-50%, -50%);
}

.stack-gray-segment {
  min-width: 0;
  flex: 1 1 0;
  background: var(--color-muted);
}

.stack-gray-gap {
  flex: 0 0 8px;
  background: transparent;
}

.stack-break img {
  width: 24px;
  height: 24px;
  filter: drop-shadow(0 0 1px var(--color-background));
}
</style>
